import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API, rupiah, fmtDate, ORDER_STATUS } from "@/lib/api";
import { Wallet, ShoppingBag, Users, Package, AlertTriangle, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    API.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-slate-500">Memuat dashboard...</p>;

  const kpis = [
    { label: "Total Penjualan", value: rupiah(data.penjualan_total), icon: Wallet, testid: "kpi-penjualan" },
    { label: "Total Pesanan", value: data.pesanan_total, icon: ShoppingBag, testid: "kpi-pesanan" },
    { label: "Pelanggan Terdaftar", value: data.pelanggan_total, icon: Users, testid: "kpi-pelanggan" },
    { label: "Saldo Kas", value: rupiah(data.saldo_kas), icon: Package, testid: "kpi-saldo" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0B1F3A]">Dashboard</h1>
          <p className="text-sm text-slate-500">Ringkasan bisnis NAWADAH by NDH Official hari ini</p>
        </div>
        {data.perlu_tindakan > 0 && (
          <Link to="/admin/pesanan" data-testid="action-needed" className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 text-xs font-bold rounded-md hover:bg-amber-200 transition-colors">
            <AlertTriangle className="h-4 w-4" /> {data.perlu_tindakan} pesanan perlu tindakan
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => (
          <div key={k.label} data-testid={k.testid} className="bg-white border border-slate-200 rounded-md p-5 fade-in-up" style={{ animationDelay: `${i * 0.08}s` }}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{k.label}</p>
              <k.icon className="h-4 w-4 text-[#C9A227]" />
            </div>
            <p className="text-2xl font-bold text-[#0B1F3A]">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h2 className="font-bold text-sm">Pesanan Terbaru</h2>
            <Link to="/admin/pesanan" className="text-xs font-bold text-[#C9A227] inline-flex items-center gap-1 hover:underline">
              Semua <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="divide-y divide-slate-100" data-testid="recent-orders">
            {data.pesanan_terbaru.length === 0 && <p className="px-5 py-8 text-sm text-slate-400">Belum ada pesanan.</p>}
            {data.pesanan_terbaru.map((o) => {
              const st = ORDER_STATUS[o.status] || { label: o.status, color: "bg-slate-100" };
              return (
                <div key={o.id} className="px-5 py-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="font-mono-accent text-xs font-bold">{o.order_number}</p>
                    <p className="text-xs text-slate-400">{o.customer_name} • {fmtDate(o.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">{rupiah(o.total)}</p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h2 className="font-bold text-sm">Stok Menipis (≤5)</h2>
            <Link to="/admin/stok" className="text-xs font-bold text-[#C9A227] inline-flex items-center gap-1 hover:underline">
              Kelola <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="divide-y divide-slate-100" data-testid="low-stock">
            {data.stok_menipis.length === 0 && <p className="px-5 py-8 text-sm text-slate-400">Semua stok aman.</p>}
            {data.stok_menipis.map((s, i) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between">
                <p className="text-sm">{s.product} <span className="text-slate-400">({s.variant})</span></p>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Sisa {s.stock}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
